import { useState, useRef, useEffect } from 'react'
import { User } from 'lucide-react'
import { cn } from '../atoms'
import { useApi } from '../../store/auth.store'

interface Contact {
  email: string
  count: number
}

interface EmailAutocompleteProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  id?: string
  ariaLabel?: string
}

/**
 * Input de email con autocompletado estilo Gmail.
 * Mantiene el estilo original (input plano sin chips).
 * Muestra sugerencias de contactos de la BD en un dropdown.
 */
export function EmailAutocomplete({
  value,
  onChange,
  placeholder = 'destinatario@correo.com',
  className,
  disabled = false,
  id,
  ariaLabel,
}: EmailAutocompleteProps) {
  const api = useApi()
  const apiRef = useRef(api)
  apiRef.current = api
  const [query, setQuery] = useState('')
  const [suggestions, setSuggestions] = useState<Contact[]>([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const [isLoading, setIsLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()
  const justSelected = useRef(false)

  // Buscar contactos con debounce
  useEffect(() => {
    if (justSelected.current) { justSelected.current = false; return }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (query.length < 2) { setSuggestions([]); return }
    let cancelled = false
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await apiRef.current.get<Contact[]>(`/emails/contacts?q=${encodeURIComponent(query)}`)
        if (!cancelled) setSuggestions(results || [])
      } catch {
        if (!cancelled) setSuggestions([])
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }, 250)
    setIsLoading(true)
    return () => { cancelled = true; if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [query])

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const insertEmail = (email: string) => {
    // Insertar el email en la posición correcta del value
    // Buscar la última palabra parcial (después de la última coma)
    const lastComma = value.lastIndexOf(',')
    const lastSemicolon = value.lastIndexOf(';')
    const lastSep = Math.max(lastComma, lastSemicolon)
    const before = lastSep >= 0 ? value.substring(0, lastSep + 1).trim() + ' ' : ''
    const newValue = before ? `${before}${email}, ` : `${email}, `
    onChange(newValue.trim())
    setQuery('')
    setSuggestions([])
    setShowDropdown(false)
    justSelected.current = true
    requestAnimationFrame(() => inputRef.current?.focus())
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIndex(i => Math.min(i + 1, suggestions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIndex(i => Math.max(i - 1, -1))
    } else if (e.key === 'Enter' && highlightIndex >= 0 && suggestions[highlightIndex]) {
      e.preventDefault()
      insertEmail(suggestions[highlightIndex].email)
    } else if (e.key === 'Escape') {
      setShowDropdown(false)
      setHighlightIndex(-1)
    } else if (e.key === 'Tab' && highlightIndex >= 0 && suggestions[highlightIndex]) {
      e.preventDefault()
      insertEmail(suggestions[highlightIndex].email)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    onChange(val)

    // Extraer la última palabra (posible email parcial)
    const parts = val.split(/[,;]+/)
    const lastPart = (parts[parts.length - 1] || '').trim()
    setQuery(lastPart)
    setHighlightIndex(-1)
    setShowDropdown(true)
  }

  return (
    <div ref={containerRef} className={cn('relative flex-1', className)}>
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => query.length >= 2 && setShowDropdown(true)}
        placeholder={placeholder}
        disabled={disabled}
        className="block w-full text-sm border-0 bg-transparent focus:ring-0 focus:outline-none text-gray-900 placeholder:text-gray-400"
        aria-label={ariaLabel || 'Destinatario'}
        aria-autocomplete="list"
        aria-expanded={showDropdown && suggestions.length > 0}
        role="combobox"
        autoComplete="off"
      />

      {/* Loading indicator */}
      {isLoading && query.length >= 2 && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2">
          <div className="w-3.5 h-3.5 border-2 border-gray-300 border-t-primary-500 rounded-full animate-spin" />
        </div>
      )}

      {/* Dropdown de sugerencias */}
      {showDropdown && suggestions.length > 0 && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 max-h-56 overflow-y-auto"
          role="listbox"
        >
          <div className="p-1">
            {suggestions.map((contact, i) => (
              <button
                key={contact.email}
                type="button"
                onClick={() => insertEmail(contact.email)}
                onMouseEnter={() => setHighlightIndex(i)}
                className={cn(
                  'flex items-center gap-3 w-full px-3 py-2 text-left rounded-lg transition-colors',
                  i === highlightIndex
                    ? 'bg-primary-50'
                    : 'hover:bg-gray-50',
                )}
                role="option"
                aria-selected={i === highlightIndex}
              >
                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-gray-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 truncate">{contact.email}</p>
                </div>
                <span className="text-[10px] text-gray-400 tabular-nums flex-shrink-0">
                  {contact.count}×
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
